// E8RootZeroExpressionClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 3) – cztery liczby z pierwiastkami,
// nalezy wskazac te rowna 0.

import React from "react";
import { ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const VARS = ["w", "x", "y", "z"];

// jednostki pierwiastkowe: rad, root = sqrt(rad) (radykand to liczba kwadratowa)
const UNITS = [
  { rad: "9+16", root: 5 },
  { rad: "16+9", root: 5 },
  { rad: "36+64", root: 10 },
  { rad: "64+36", root: 10 },
  { rad: "25-16", root: 3 },
  { rad: "25-9", root: 4 },
  { rad: "100-64", root: 6 },
  { rad: "100-36", root: 8 },
  { rad: "169-144", root: 5 },
  { rad: "169-25", root: 12 },
];

function randInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function atomLatex(unit, R, order) {
  return order === "rootFirst"
    ? `\\sqrt{${unit.rad}}-${R}`
    : `${R}-\\sqrt{${unit.rad}}`;
}

function buildData({ atoms }) {
  const correctIndex = atoms.findIndex((a) => a.value === 0);
  return {
    atoms, // [{label, rad, root, R, order, latex, value}]
    correctIndex,
  };
}

function buildOrderMix() {
  const rootFirstCount = [1, 2, 3][Math.floor(Math.random() * 3)];
  return shuffle([
    ...Array(rootFirstCount).fill("rootFirst"),
    ...Array(4 - rootFirstCount).fill("constFirst"),
  ]);
}

function makeAtom(label, unit, { zero, order }) {
  const chosenOrder = order;
  let R;
  if (zero) {
    R = unit.root; // value = 0
  } else if (chosenOrder === "rootFirst") {
    // sqrt - R, dodatnie => R < root
    R = randInt(1, unit.root - 1);
  } else {
    // R - sqrt, dodatnie => R > root
    R = unit.root + randInt(1, 4);
  }
  const value =
    chosenOrder === "rootFirst" ? unit.root - R : R - unit.root;
  return { label, rad: unit.rad, root: unit.root, R, order: chosenOrder, latex: atomLatex(unit, R, chosenOrder), value };
}

function createOriginalTask() {
  const atoms = [
    { label: "w", rad: "100-64", root: 6, R: 2, order: "rootFirst", latex: "\\sqrt{100-64}-2", value: 4 },
    { label: "x", rad: "64+36", root: 10, R: 12, order: "constFirst", latex: "12-\\sqrt{64+36}", value: 2 },
    { label: "y", rad: "25-16", root: 3, R: 3, order: "rootFirst", latex: "\\sqrt{25-16}-3", value: 0 },
    { label: "z", rad: "9+16", root: 5, R: 7, order: "constFirst", latex: "7-\\sqrt{9+16}", value: 2 },
  ];
  return {
    type: "E8RootZeroExpressionClosedTask",
    data: buildData({ atoms }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 3,
    },
    tags: ["e8", "pierwiastki", "obliczanie-wartosci"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const units = shuffle(UNITS).slice(0, 4);
  const zeroPos = Math.floor(Math.random() * 4);
  const orderMix = buildOrderMix();
  const atoms = units.map((unit, i) =>
    makeAtom(VARS[i], unit, { zero: i === zeroPos, order: orderMix[i] })
  );

  return {
    type: "E8RootZeroExpressionClosedTask",
    data: buildData({ atoms }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_3" },
    tags: ["e8", "pierwiastki", "obliczanie-wartosci"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Dane są cztery liczby:</p>
      <div style={{ display: "grid", gap: 6, margin: "6px 0 10px", paddingLeft: 8 }}>
        {data.atoms.map((atom) => (
          <div key={atom.label}>
            <InlineMath math={`${atom.label}=${atom.latex}`} />
          </div>
        ))}
      </div>
      <p>
        Która z tych liczb jest równa <InlineMath math="0" />? Wybierz właściwą
        odpowiedź spośród podanych.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.atoms.map((atom, index) => (
          <div key={atom.label}>
            <ChoiceOptionMath letter={LETTERS[index]} math={atom.label} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.atoms.map((atom, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  const atom = data.atoms[data.correctIndex];
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={atom.label} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const atom = data.atoms[userAnswer];
  if (!atom) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={atom.label} />
    </span>
  );
}

function atomSteps(atom) {
  return atom.order === "rootFirst"
    ? `${atom.label}=\\sqrt{${atom.rad}}-${atom.R}=${atom.root}-${atom.R}=${atom.value}`
    : `${atom.label}=${atom.R}-\\sqrt{${atom.rad}}=${atom.R}-${atom.root}=${atom.value}`;
}

export function renderExplanation({ data }) {
  const zero = data.atoms[data.correctIndex];
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Obliczamy wartość każdej liczby — najpierw
        pierwiastek z liczby kwadratowej, potem działanie.
      </p>
      <div style={{ display: "grid", gap: 6, margin: "6px 0" }}>
        {data.atoms.map((atom) => (
          <div key={atom.label}>
            <InlineMath math={atomSteps(atom)} />
          </div>
        ))}
      </div>
      <p>
        <strong>Krok 2.</strong> Równa <InlineMath math="0" /> jest liczba{" "}
        <InlineMath math={zero.label} />, więc poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
